import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
  useState
} from "react";

import Modal from "../../components/atoms/modal";
import ProctoringWindow from "./_components/proctoring/proctoringWindow";
import Assessment from "./assessment";
import ScheduleNotStarted from "./_components/pre-assessment/access-variant-screens/scheduleNotStarted";
import ScheduleTimeCompleted from "./_components/pre-assessment/access-variant-screens/scheduleTimeCompleted";
import LoginWindowCompleted from "./_components/pre-assessment/access-variant-screens/loginWindowCompleted";
import MaxAttemptExceeded from "./_components/pre-assessment/access-variant-screens/maxAttemptExeceeded";
import InProgress from "./_components/pre-assessment/access-variant-screens/inProgress";
import ProgressNotStarted from "./_components/pre-assessment/access-variant-screens/progress-not-started/progressNotStarted";
import AttemptResultProcessing from "./_components/pre-assessment/access-variant-screens/attemptResultProcessing";

import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { AccessStatus } from "@assessment/types/accessStatus";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { ProctoringFunctionsContext } from "@assessment/contexts/proctoringFunctionsContext";
import {
  useLazyFinishOngoingAssessmentQuery,
  useLazyHeartBeatApiQuery
} from "@assessment/store/slices/api/assessmentApiSlice";

import {
  exitFullScreen,
  requestFullScreen
} from "../../utils/global/fullScreenFunctions";
import { cn } from "@assessment/utils/cn";
import { useShowErrors } from "./_components/toasterWrapper";
import { BrowserProctoringFunctionsContext } from "@assessment/contexts/browserProctoringFunctionContext";
import WaterMark from "@assessment/components/molecules/watermark";
import { useIdleTimer } from "react-idle-timer";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";
import { useBrowserProctoring } from "@assessment/hooks/useBrowserProctoring";
import AssessmentCancelled from "./_components/pre-assessment/access-variant-screens/assessmentCancelled";
import NotScheduled from "./_components/pre-assessment/access-variant-screens/notScheduled";
import CoolOffPeriodScreen from "./_components/pre-assessment/access-variant-screens/coolOffPeriodScreen";
interface AssessmentWrapperProps {
  browserProctoring: boolean;
  videoProctoring: boolean;
}

const AssessmentWrapper = ({
  browserProctoring,
  videoProctoring
}: AssessmentWrapperProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const showErrors = useShowErrors();

  const [finishOngoingAssessment] = useLazyFinishOngoingAssessmentQuery();
  const [heartBeatApi] = useLazyHeartBeatApiQuery();

  const { scheduleId } = useParams();
  const context = useContext(ProctoringFunctionsContext);
  if (!context) {
    throw new Error("Context Error");
  }
  const {
    stopProctoring,
    retryProctoring,
    sessionState,
    failureReason,
    canRetry
  } = context;

  const [showVideoProctoringBlockedModal, setShowVideoProctoringBlockedModal] =
    useState(false);

  const assessmentRef = useRef<HTMLDivElement | null>(null);

  const showProctoringVideoPreview = useAppSelector(
    (state) => state.AssessmentSlice.proctoring.showProctoringVideoPreview
  );

  const constants = useAppSelector((store) => store.AssessmentSlice.duration);

  const showWarningModal = useAppSelector(
    (state) => state.AssessmentSlice.modalFlags.showWarningModal
  );
  const assessmentHasStarted = useAppSelector(
    (state) => state.AssessmentSlice.assessmentHasStarted
  );

  const showWatermark = useAppSelector(
    (state) =>
      state.AssessmentSlice.preAssessmentData?.questionConfig?.showWatermark
  );

  const accessDetails = useAppSelector(
    (state) => state.AssessmentSlice.accessDetails
  );

  const showMarks = useAppSelector(
    (store) => store.AssessmentSlice.preAssessmentData?.showResults
  );

  const feedbackData = useAppSelector(
    (store) => store.AssessmentSlice.preAssessmentData?.socialInteractions
  );
  const feedbackAllowed = useAppSelector(
    (store) => store.AssessmentSlice.preAssessmentData?.feedbackAllowed
  );
  const questionType = useAppSelector(
    (store) => store.AssessmentSlice?.questionType
  );

  const isCodingQues = questionType === "coding";

  const ongoingId = useAppSelector((state) => state.AssessmentSlice.ongoingId);
  const user = useAppSelector((state) => state.UserInfoSlice.profile);

  const orgBasedFeatureName =
    orgBasedFeatureNameMapping[(user?.org as string) || "default"] ||
    orgBasedFeatureNameMapping["default"];

  const videoProctoringFailureMessage = useMemo(() => {
    switch (failureReason) {
      case "camera_permission_denied":
        return "Camera access was revoked. Please allow camera access and retry proctoring.";
      case "camera_unavailable":
        return "Camera is unavailable right now. Please reconnect/select camera and retry proctoring.";
      case "facemesh_init_failed":
        return "Face tracking failed. Retry proctoring to continue.";
      case "coco_load_failed":
        return "Proctoring engine is unavailable. You cannot continue securely without it.";
      case "stream_lost_reacquire_failed":
        return "Camera feed was lost and could not be recovered automatically.";
      case "calibration_insufficient_data":
        return "Calibration quality is insufficient. Retry proctoring to continue.";
      case "detection_cadence_unavailable":
        return "Proctoring timing configuration is unavailable. Retry proctoring.";
      default:
        return "Secure proctoring is unavailable. Retry proctoring or submit your assessment.";
    }
  }, [failureReason]);

  const showWarningModalHandler = useCallback(
    (val: boolean) => {
      dispatch(AssessmentSliceActions.mutateShowWarningModal(val));
    },
    [dispatch]
  );

  const { stopBrowserProctoring } = useBrowserProctoring({
    assessmentRef,
    browserProctoring,
    assessmentHasStarted,
    isCodingQues,
    ongoingId,
    scheduleId,
    onRequireWarningModal: () => showWarningModalHandler(true)
  });

  const exitFullScreenHandler = () => {
    exitFullScreen(dispatch);
  };

  const enterFullScreenHandler = () => {
    requestFullScreen(dispatch);
  };

  const assessmentSubmitHandler = async () => {
    try {
      if (!scheduleId) {
        throw {
          status: "",
          title: "Schedule Id Not Found",
          err: {},
          description: ""
        };
      }

      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      const res = await finishOngoingAssessment({
        ongoingId: ongoingId,
        scheduleId: scheduleId
      });

      if (res.error) {
        throw {
          status: res.status,
          title: `Error finishing the ${orgBasedFeatureName}.`,
          err: res.error,
          description: ""
        };
      }

      const obtainedMarks = res?.data?.data?.obtainedMarks;
      const totalMarks = res?.data?.data?.totalMarks;
      const showScore = showMarks.includes("score") ? true : false;
      const showPercentage = showMarks.includes("percentage") ? true : false;

      stopProctoring();
      stopBrowserProctoring();
      exitFullScreenHandler();
      navigate(`/assessment/feedback/${scheduleId}?ongoingId=${ongoingId}`, {
        state: {
          marksObtained: obtainedMarks,
          totalMarks: totalMarks,
          ongoingId: ongoingId,
          feedbackData: feedbackData,
          showScore: showScore,
          showPercentage: showPercentage,
          showMarks: showMarks,
          feedbackAllowed: feedbackAllowed,
          feedbackId: feedbackData[0]?.contentId
        }
      });
      dispatch(AssessmentSliceActions.resetState());
    } catch (err: unknown) {
      console.error(err);
      const temp = err as {
        title?: string;
        description?: string;
        err?: { data?: { error?: string[] } };
      };
      const rawErrList = temp?.err?.data?.error;
      const errorTitle = temp?.title ?? "Something went wrong";
      const errList: string[] = (rawErrList ?? []).map(
        (el: string) => el.split("_")[0]
      );
      if (showErrors)
        showErrors(errorTitle, errList, "error", temp?.description);
    }
  };

  const heartBeatApiCall = useCallback(async () => {
    try {
      if (!scheduleId) {
        throw {
          status: "",
          title: "Schedule Id Not Found",
          err: {},
          description: ""
        };
      }
      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }
      const heartBeatRes = await heartBeatApi({
        ongoingId: ongoingId,
        scheduleId: scheduleId
      });
      if (heartBeatRes?.data?.data?.ongoingQPDuration) {
        dispatch(
          AssessmentSliceActions.mutateTimeLeft(
            heartBeatRes?.data?.data?.ongoingQPDuration
          )
        );
      }
    } catch (error: unknown) {
      console.log("error", error);
    }
  }, [scheduleId, ongoingId, heartBeatApi, dispatch]);

  useEffect(() => {
    void heartBeatApiCall();
  }, [heartBeatApiCall]);

  const onIdle = useCallback(() => {
    void heartBeatApiCall();
  }, [heartBeatApiCall]);

  useIdleTimer({
    timeout: (constants?.idle ?? 180) * 1000,
    onIdle,
    debounce: 500
  });

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;

    if (showWarningModal) {
      intervalId = setInterval(
        () => {
          void heartBeatApiCall();
        },
        (constants?.idle ?? 180) * 1000
      );
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showWarningModal, constants?.idle, heartBeatApiCall]);

  useEffect(() => {
    if (!videoProctoring || !assessmentHasStarted) {
      setShowVideoProctoringBlockedModal(false);
      return;
    }

    const isBlockedState =
      sessionState === "recoverable_error" || sessionState === "fatal_error";

    if (isBlockedState) {
      setShowVideoProctoringBlockedModal(true);
    } else {
      setShowVideoProctoringBlockedModal(false);
    }
  }, [videoProctoring, assessmentHasStarted, sessionState]);

  if (accessDetails.accessStatus === AccessStatus.SCHEDULE_HAS_BEEN_CANCELLED) {
    const candidateWasInvited = Boolean(
      accessDetails?.ongoingId || ongoingId || accessDetails?.attempts
    );
    return (
      <BrowserProctoringFunctionsContext.Provider
        value={{ stopBrowserProctoring }}
      >
        {candidateWasInvited ? (
          <AssessmentCancelled title={accessDetails?.title} />
        ) : (
          <NotScheduled />
        )}
      </BrowserProctoringFunctionsContext.Provider>
    );
  }

  return (
    <BrowserProctoringFunctionsContext.Provider
      value={{
        stopBrowserProctoring
      }}
    >
      <div
        ref={assessmentRef}
        className={cn(
          "relative w-full min-h-screen",
          assessmentHasStarted
            ? ""
            : "flex items-center justify-center py-4 px-4 sm:px-6 lg:px-12 bg-profile-bkg"
        )}
      >
        {/* Proctoring Preview */}
        <div className="fixed bottom-5 left-5 z-[999]">
          {showProctoringVideoPreview &&
            assessmentHasStarted &&
            videoProctoring && <ProctoringWindow />}
        </div>

        {/* Pre-Assessment Content */}
        {!assessmentHasStarted && (
          <div className="personal-scroll-wrapper w-full max-w-[1400px] bg-bkg rounded-[30px] flex flex-col lg:flex-row gap-6 overflow-hidden shadow-md">
            {accessDetails.accessStatus ===
              AccessStatus.SCHEDULE_NOT_STARTED && (
              <ScheduleNotStarted
                orgBasedFeatureName={orgBasedFeatureName || "assessment"}
              />
            )}
            {accessDetails.accessStatus ===
              AccessStatus.SCHEDULE_TIME_COMPLETED && (
              <ScheduleTimeCompleted
                orgBasedFeatureName={orgBasedFeatureName || "assessment"}
              />
            )}
            {accessDetails.accessStatus ===
              AccessStatus.LOGIN_WINDOW_COMPLETED && (
              <LoginWindowCompleted
                orgBasedFeatureName={orgBasedFeatureName || "assessment"}
              />
            )}
            {accessDetails.accessStatus ===
              AccessStatus.MAX_ATTEMPT_EXCEEDED && (
              <MaxAttemptExceeded
                orgBasedFeatureName={orgBasedFeatureName || "assessment"}
              />
            )}
            {accessDetails.accessStatus ===
              AccessStatus.ATTEMPT_IN_RESULT_PROCESSING && (
              <AttemptResultProcessing
                orgBasedFeatureName={orgBasedFeatureName || "assessment"}
              />
            )}
            {accessDetails.accessStatus === AccessStatus.IN_PROGRESS && (
              <InProgress />
            )}
            {accessDetails.accessStatus ===
              AccessStatus.PROGRESS_NOT_STARTED && (
              <ProgressNotStarted
                orgBasedFeatureName={orgBasedFeatureName || "assessment"}
              />
            )}
            {accessDetails.accessStatus ===
              AccessStatus.COOL_OFF_PERIOD_NOT_FINISHED && (
              <ProgressNotStarted
                orgBasedFeatureName={orgBasedFeatureName || "assessment"}
              />
            )}
          </div>
        )}

        {/* Assessment Content */}
        {assessmentHasStarted && (
          <>
            <Assessment />
            {showWatermark && <WaterMark waterMarkText={user?.email} />}
          </>
        )}

        {/* Warning Modal */}
        <Modal
          open={showWarningModal}
          dialogueClassName="max-w-[600px]"
          childDialogTitle={t("assessment.wrapper.warningTitle")}
          closeBtn={true}
          children={
            <div className="bg-bkg p-6">
              <p className="mt-4">
                {t("assessment.wrapper.stayOnPageMessage", {
                  featureName: orgBasedFeatureName
                })}
              </p>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => {
                    // heartBeatApiCall();
                    showWarningModalHandler(false);
                    enterFullScreenHandler();
                  }}
                  className="px-4 py-2 text-white rounded-md bg-primary"
                >
                  {t("assessment.wrapper.iUnderstand")}
                </button>
                <button
                  onClick={assessmentSubmitHandler}
                  className="px-4 py-2 text-white bg-red-500 rounded-md"
                >
                  {t("assessment.wrapper.submitTheFeature", {
                    featureName: orgBasedFeatureName
                  })}
                </button>
              </div>
            </div>
          }
        />
        <Modal
          open={showVideoProctoringBlockedModal}
          dialogueClassName="max-w-[650px]"
          childDialogTitle={t("assessment.wrapper.videoProctoringInterrupted")}
          closeBtn={true}
          children={
            <div className="bg-bkg p-6">
              <p className="mt-2 text-secondary">
                {videoProctoringFailureMessage}
              </p>
              <p className="mt-3 text-sm text-secondary/80">
                {t("assessment.wrapper.continuationBlocked")}
              </p>
              <div className="flex justify-end gap-4 mt-6">
                {canRetry && (
                  <button
                    onClick={() => {
                      void retryProctoring();
                    }}
                    className="px-4 py-2 text-white rounded-md bg-primary"
                  >
                    {t("assessment.wrapper.retryProctoring")}
                  </button>
                )}
                <button
                  onClick={assessmentSubmitHandler}
                  className="px-4 py-2 text-white bg-red-500 rounded-md"
                >
                  {t("assessment.wrapper.submitTheFeature", {
                    featureName: orgBasedFeatureName
                  })}
                </button>
              </div>
            </div>
          }
        />
      </div>
    </BrowserProctoringFunctionsContext.Provider>
  );
};

export default AssessmentWrapper;
